/**
 * Agent catalogue from config, plus a render-layer label resolver.
 *
 * The launchable-agent list is config-driven (GET /config `agents`) — a new
 * agent needs no frontend release. `useAgentCatalogue` returns the raw,
 * unordered list (empty when the backend omits the block on older deployments).
 * `useAgentLabel` maps a slug to its localised display name.
 */

import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useConfigStore } from '@/stores/config'
import { useAuthStore } from '@/stores/auth'
import { api } from '../api/client'
import type { AgentInfo } from '@/api/types'

// Module-level const so the selector never returns a fresh array reference each
// render (an inline `?? []` would loop React error #185). See usePatientsConfig.
const EMPTY_AGENTS: AgentInfo[] = []

/** Raw, unordered agent catalogue from config (empty when absent). */
export function useAgentCatalogue(): AgentInfo[] {
  return useConfigStore((s) => s.config?.agents ?? EMPTY_AGENTS)
}

/**
 * The catalogue minus agents the caller cannot open — what every user-facing
 * agent LIST (launcher, settings) should render, so a nurse never sees
 * roster/allocation/team-access tiles that 403 on tap.
 *
 * This does not decide anything: GET /me `launchable_agents` is the server's
 * complete answer (the open catalogue plus every restricted agent the caller
 * holds `agent:<slug> viewer` on, via OpenFGA), and this intersects it with the
 * unauthenticated GET /config catalogue that carries the labels. The two lists
 * come from different endpoints only because /config is public.
 *
 * It replaced a client-side re-derivation of the access rule — "gated, unless
 * you manage some unit, unless you hold a grant" — which had to be kept in step
 * with a server-side gate written in different terms, and drifted the moment
 * either moved.
 *
 * Until the answer arrives — a profile cached from before the field existed, or
 * a `/me` still in flight against a cold api-service — the open catalogue shows
 * and the restricted agents do not. That is not a second policy: an agent
 * config does not mark `restricted` is open to every authenticated user, which
 * the config we already hold says outright and no ACL is asked about. The
 * restricted ones stay hidden until someone competent says otherwise, and the
 * server re-enforces every one of them at session creation regardless.
 */
export function useLaunchableAgentCatalogue(): AgentInfo[] {
  const launchable = useAuthStore((s) => s.user?.launchableAgents)
  const catalogue = useAgentCatalogue()
  return useMemo(
    () => catalogue.filter((a) => (launchable ? launchable.includes(a.slug) : !a.restricted)),
    [catalogue, launchable],
  )
}

// The server's launchable answer, fetched from GET /agents — the
// permission-filtered list the session create gate enforces — and stored on
// the auth user as `launchableAgents`. /me no longer carries the list
// (care-app/ADR-0015); this fetch is its one home. Fire-and-forget after
// boot: until it lands the launcher shows the open catalogue, exactly as it
// always did while a /me was in flight. A failure leaves the previous
// answer in place — no store write — so a transient 503 never narrows the
// launcher; the gate re-enforces every tile at session creation regardless.
export async function fetchLaunchableAgents(): Promise<void> {
  // Captured before the fetch for the same reason refreshAdminTier captures
  // its token: on an identity swap an older token's /agents can still be in
  // flight, and this write is not merged — it would replace whoever is
  // current with the outgoing account's launchable list.
  const tokenAtFetch = useAuthStore.getState().accessToken
  try {
    const rows = await api.get<{ slug: string }[]>('/agents')
    const slugs = rows.map((r) => r.slug)
    const { user, accessToken, setAuth } = useAuthStore.getState()
    if (user && accessToken === tokenAtFetch) {
      setAuth(accessToken ?? '', {
        ...user,
        launchableAgents: slugs,
      })
    }
  } catch {
    // No store write: the previous answer stands (fail-open on display, the
    // server gate re-enforces everything anyway).
  }
}

/**
 * Render-layer localiser for an agent's user-facing display name.
 *
 * The config `name` is the English specialist title (e.g. "Y-Site Compatibility
 * Specialist"); the card/launcher/strip show the friendly, localised
 * label (`agent.<slug>.name`). House rule: German via i18n, never a raw config
 * string. The config name is the `defaultValue` fallback, so an unknown/future
 * agent (e.g. AI Studio) still renders its config name with no frontend release.
 */
export function useAgentLabel(): (slug: string, fallbackName: string) => string {
  const { t } = useTranslation()
  return useCallback(
    (slug, fallbackName) => t(`agent.${slug}.name`, { defaultValue: fallbackName }),
    [t],
  )
}
